import React, { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  Chip,
  Stack,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
  Alert,
  Skeleton,
} from '@mui/material';
import { Search as SearchIcon, HelpOutline as EmptyIcon, Visibility as ViewIcon } from '@mui/icons-material';
import { debounce } from 'lodash';

import MainCard from '../../components/MainCard';
import { CustomTableHead, CustomTableHeadCell, CustomTableRow } from '../../components/@extended/CustomTable';
import TablePagination from '../../components/common/TablePagination';
import { useGetSupportTicketsQuery } from '../../store/slices/apiSlice';
import {
  STATUS_OPTIONS_WITH_ALL,
  PROBLEM_TYPES,
  getStatusConfig,
} from '../../util/supportTicketConstants';

const StatusChip = ({ status }) => {
  const { label, color, text } = getStatusConfig(status);
  return (
    <Chip
      label={label}
      size="small"
      sx={{ backgroundColor: color, color: text, fontWeight: 500, fontSize: '0.75rem' }}
    />
  );
};

const SupportTicketList = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [problemTypeFilter, setProblemTypeFilter] = useState('');
  const [createdAtFrom, setCreatedAtFrom] = useState('');
  const [createdAtTo, setCreatedAtTo] = useState('');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('DESC');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const debouncedSetSearch = useMemo(
    () =>
      debounce((value) => {
        setDebouncedSearch(value);
        setPage(0);
      }, 300),
    [],
  );

  const handleSearchChange = useCallback(
    (e) => {
      setSearchQuery(e.target.value);
      debouncedSetSearch(e.target.value);
    },
    [debouncedSetSearch],
  );

  const handleStatusChange = (e) => {
    setStatusFilter(e.target.value);
    setPage(0);
  };

  const handleProblemTypeChange = (e) => {
    setProblemTypeFilter(e.target.value);
    setPage(0);
  };

  const handlePageChange = (newPage, newRowsPerPage) => {
    setPage(newPage);
    setRowsPerPage(newRowsPerPage);
  };

  const { data, isLoading, error } = useGetSupportTicketsQuery({
    page: page + 1,
    limit: rowsPerPage,
    status: statusFilter || undefined,
    problemType: problemTypeFilter || undefined,
    search: debouncedSearch || undefined,
    createdAtFrom: createdAtFrom || undefined,
    createdAtTo: createdAtTo || undefined,
    sortBy,
    sortOrder,
  });

  const tickets = data?.data ?? [];
  const totalCount = data?.total ?? 0;
  const hasFilters = debouncedSearch || statusFilter || problemTypeFilter;

  const handleView = (ticket) => navigate(`/admin/support-tickets/${ticket.id}`);

  return (
    <MainCard
      title={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h5">Support Tickets</Typography>
        </Box>
      }
    >
      <Box sx={{ mb: 3 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          flexWrap="wrap"
        >
            <TextField
              placeholder="Search by name, email, organization..."
              value={searchQuery}
              onChange={handleSearchChange}
              size="small"
              sx={{ minWidth: { xs: '100%', sm: 280 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
            <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 160 } }}>
              <InputLabel>Status</InputLabel>
              <Select value={statusFilter} onChange={handleStatusChange} label="Status">
                {STATUS_OPTIONS_WITH_ALL.map((opt) => (
                  <MenuItem key={opt.value || 'all'} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 220 } }}>
              <InputLabel>Problem type</InputLabel>
              <Select value={problemTypeFilter} onChange={handleProblemTypeChange} label="Problem type">
                <MenuItem value="">All</MenuItem>
                {PROBLEM_TYPES.map((opt) => (
                  <MenuItem key={opt} value={opt}>
                    {opt}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              size="small"
              label="From date"
              type="date"
              value={createdAtFrom}
              onChange={(e) => { setCreatedAtFrom(e.target.value); setPage(0); }}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: { xs: '100%', sm: 150 } }}
            />
            <TextField
              size="small"
              label="To date"
              type="date"
              value={createdAtTo}
              onChange={(e) => { setCreatedAtTo(e.target.value); setPage(0); }}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: { xs: '100%', sm: 150 } }}
            />
            <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 130 } }}>
              <InputLabel>Sort by</InputLabel>
              <Select value={sortBy} onChange={(e) => { setSortBy(e.target.value); setPage(0); }} label="Sort by">
                <MenuItem value="createdAt">Created</MenuItem>
                <MenuItem value="updatedAt">Updated</MenuItem>
                <MenuItem value="status">Status</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 110 } }}>
              <InputLabel>Order</InputLabel>
              <Select value={sortOrder} onChange={(e) => { setSortOrder(e.target.value); setPage(0); }} label="Order">
                <MenuItem value="DESC">Newest first</MenuItem>
                <MenuItem value="ASC">Oldest first</MenuItem>
              </Select>
            </FormControl>
        </Stack>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load support tickets. Please try again.
        </Alert>
      )}

      {isLoading ? (
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <CustomTableHead>
              <TableRow>
                <CustomTableHeadCell>User</CustomTableHeadCell>
                <CustomTableHeadCell>Email</CustomTableHeadCell>
                <CustomTableHeadCell>Organization</CustomTableHeadCell>
                <CustomTableHeadCell>Problem type</CustomTableHeadCell>
                <CustomTableHeadCell>Status</CustomTableHeadCell>
                <CustomTableHeadCell>Created</CustomTableHeadCell>
                <CustomTableHeadCell align="right">Actions</CustomTableHeadCell>
              </TableRow>
            </CustomTableHead>
            <TableBody>
              {[1, 2, 3, 4, 5].map((row) => (
                <TableRow key={row}>
                  <TableCell><Skeleton variant="text" width="80%" /></TableCell>
                  <TableCell><Skeleton variant="text" width="70%" /></TableCell>
                  <TableCell><Skeleton variant="text" width="60%" /></TableCell>
                  <TableCell><Skeleton variant="text" width="50%" /></TableCell>
                  <TableCell><Skeleton variant="rounded" width={80} height={24} /></TableCell>
                  <TableCell><Skeleton variant="text" width={90} /></TableCell>
                  <TableCell><Skeleton variant="text" width={40} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : tickets.length === 0 ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8, px: 2, textAlign: 'center' }}>
          <EmptyIcon sx={{ fontSize: 80, color: 'grey.300', mb: 2 }} />
          <Typography variant="h5" color="text.secondary" gutterBottom>
            {hasFilters ? 'No tickets match your filters' : 'No support tickets yet'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {hasFilters ? 'Try adjusting your search or filters.' : 'Tickets created from the login or Help & Support page will appear here.'}
          </Typography>
        </Box>
      ) : isMobile ? (
        <Stack spacing={1.5}>
          {tickets.map((t) => (
            <Card
              key={t.id}
              variant="outlined"
              sx={{
                cursor: 'pointer',
                transition: 'box-shadow 0.2s, border-color 0.2s',
                '&:hover': { boxShadow: 1, borderColor: 'primary.main' },
              }}
              onClick={() => handleView(t)}
            >
              <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={0.5} sx={{ mb: 0.5 }}>
                  <Typography variant="body2" fontWeight={500}>
                    {t.userName}
                  </Typography>
                  <StatusChip status={t.status} />
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  {t.email}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t.organizationName} · {t.problemType || '–'} · {t.createdAt ? new Date(t.createdAt).toLocaleString() : '–'}
                </Typography>
              </CardContent>
            </Card>
          ))}
        </Stack>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
          <Table aria-label="Support tickets table" size="small">
            <CustomTableHead>
              <TableRow>
                <CustomTableHeadCell>User</CustomTableHeadCell>
                <CustomTableHeadCell>Email</CustomTableHeadCell>
                <CustomTableHeadCell>Organization</CustomTableHeadCell>
                <CustomTableHeadCell>Problem type</CustomTableHeadCell>
                <CustomTableHeadCell>Status</CustomTableHeadCell>
                <CustomTableHeadCell>Created</CustomTableHeadCell>
                <CustomTableHeadCell align="right">Actions</CustomTableHeadCell>
              </TableRow>
            </CustomTableHead>
            <TableBody>
              {tickets.map((t) => (
                <CustomTableRow
                  key={t.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => handleView(t)}
                >
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {t.userName}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {t.email}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{t.organizationName}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{t.problemType || '–'}</Typography>
                  </TableCell>
                  <TableCell>
                    <StatusChip status={t.status} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {t.createdAt ? new Date(t.createdAt).toLocaleString() : '–'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                    <ViewIcon fontSize="small" color="action" />
                  </TableCell>
                </CustomTableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {!isLoading && tickets.length > 0 && (
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <TablePagination
            count={totalCount}
            page={page}
            rowsPerPage={rowsPerPage}
            onNewPage={handlePageChange}
          />
        </Box>
      )}
    </MainCard>
  );
};

export default SupportTicketList;
